<template>
  <div class="quiz-modal">
    <div class="quiz-modal__modal">
      <div class="quiz-modal__wrapper">
        <TitleBar
          :title="title"
          :totalQuestions="totalQuestions"
          :currentPosition="currentPosition"
          :isQuizComplete="hasAnsweredAllQuestions"
          @finalTime="saveFinalTime"
          @closeModal="$emit('closeModal')"
        />
        <div v-if="!isLoading" class="quiz-modal__content-wrapper">
          <Question
            v-if="!isQuizComplete"
            class="quiz-modal__question"
            :currentQuestion="currentQuestion"
          />
          <Answers
            v-if="!isQuizComplete"
            class="quiz-modal__answers"
            :currentQuestion="currentQuestion"
            :lastQuestion="finalQuestion"
            @answerSelected="commitAnswer"
          />
          <Completed
            v-if="isQuizComplete"
            class="quiz-modal__completed"
            :isQuizPassed="isQuizPassed"
            :finalTime="finalTime"
            :nextVideo="nextVideo"
            :finalScore="finalScore"
            @restartQuiz="restartQuiz"
          />
        </div>
        <div v-else class="quiz-modal__loader-wrapper">
          <BarLoader
            class="quiz-modal__loader"
            color="#ef7d22"
            :size="5"
            sizeUnit="rem"
          />
        </div>
      </div>
      <ProgressBar
        v-show="!isLoading"
        class="quiz-modal__progress-bar"
        :value="percentComplete"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { BarLoader } from '@saeris/vue-spinners'
import { sendPinpointQuizBeat } from '@/services/heartbeat'
import TitleBar from '@/components/course/content/level/quiz-modal/TitleBar.vue'
import Question from '@/components/course/content/level/quiz-modal/Question.vue'
import Answers from '@/components/course/content/level/quiz-modal/Answers.vue'
import Completed from '@/components/course/content/level/quiz-modal/Completed.vue'
import ProgressBar from '@/components/layout/ProgressBar.vue'

export default {
  name: 'QuizModal',
  components: {
    TitleBar,
    Question,
    Answers,
    Completed,
    ProgressBar,
    BarLoader,
  },
  data() {
    return {
      currentQuestion: this.attempt.questions.questions[0],
      selectedAnswers: [],
      hasAnsweredAllQuestions: false,
      isQuizComplete: false,
      isQuizPassed: false,
      finalTime: null,
      finalScore: {},
      isLoading: false,
    }
  },
  props: {
    attempt: {
      type: Object,
      required: true,
    },
    quiz: {
      type: Object,
      required: true,
    },
    finishedQuiz: {
      required: false,
    },
    title: {
      type: String,
      required: true,
    },
    nextVideo: {
      type: Object,
      required: false,
    },
    videoId: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      course: 'courses/courseWithTopicLessons',
      userId: 'auth/userId',
      profile: 'auth/userProfile',
    }),
    questions() {
      const { questions } = this.attempt.questions
      return questions
    },
    totalQuestions() {
      return this.questions.length
    },
    currentPosition() {
      return this.questions.indexOf(this.currentQuestion)
    },
    finalQuestion() {
      return this.questions[this.questions.length - 1]
    },
    percentComplete() {
      if (this.finalTime) {
        return '100'
      }
      return Math.floor(
        (this.currentPosition / this.totalQuestions) * 100
      ).toString()
    },
  },
  methods: {
    commitAnswer(answer_ids) {
      const { question_id } = this.currentQuestion
      this.selectedAnswers = [
        ...this.selectedAnswers,
        { question_id, answers: answer_ids },
      ]
      this.setNextQuestion()
    },
    setNextQuestion() {
      if (this.currentQuestion === this.finalQuestion) {
        this.hasAnsweredAllQuestions = true
        this.submitAttempt()
      } else {
        this.currentQuestion = this.questions[this.currentPosition + 1]
      }
    },
    saveFinalTime(time) {
      this.finalTime = time
    },
    async submitAttempt() {
      this.isLoading = true
      const submission = this.generateSubmission()
      const payload = {
        quizId: this.quiz.id,
        attemptId: this.attempt.id,
        submission,
      }
      await this.$store.dispatch('quizzes/finishQuizAttempt', payload)
      const courseIdAndUserId = {
        courseId: this.course.id,
        userId: this.userId,
      }
      await this.$store.dispatch(
        'user/getUserCourseProgress',
        courseIdAndUserId
      )
      this.scoreQuiz()
      this.isLoading = false
    },
    generateSubmission() {
      const { questions, selectedAnswers } = this
      const submission = questions
      submission.map(question => {
        selectedAnswers.map(userAnswer => {
          if (userAnswer.question_id === question.question_id) {
            const { answers } = question
            answers.map(answer => {
              if (userAnswer.answers.includes(answer.answer_id)) {
                answer.is_selected_by_user = true
              }
            })
          }
        })
      })
      return submission
    },
    scoreQuiz() {
      const correct = []
      const incorrect = []
      const { questions } = this.finishedQuiz.questions

      questions.map(question => {
        const { answers } = question
        const totalCorrectAnswers = answers.filter(answer => answer.is_correct)
        const totalUserCorrectAnswers = answers.filter(
          answer => answer.is_selected_by_user && answer.is_correct
        )
        const totalUserIncorrectAnswers = answers.filter(
          answer => answer.is_selected_by_user && !answer.is_correct
        )

        if (
          totalCorrectAnswers.length === totalUserCorrectAnswers.length &&
          (!totalUserIncorrectAnswers || !totalUserIncorrectAnswers.length)
        ) {
          correct.push(question)
        } else {
          incorrect.push(question)
        }
      })

      this.finalScore = { correct, incorrect, questions }
      this.submitQuizAttemptToPinpoint()
      if (this.finishedQuiz.score >= 0.7) this.isQuizPassed = true
      this.isQuizComplete = true
    },
    submitQuizAttemptToPinpoint() {
      const { uaaId: userId, account_id, team_ids } = this.profile
      const { videoId, finalTime } = this
      const { id: quizId } = this.quiz
      const { score } = this.finishedQuiz
      const { id: courseId, slug, instructor_id: instructorId } = this.course
      sendPinpointQuizBeat(
        userId,
        account_id,
        team_ids,
        quizId,
        score,
        finalTime,
        videoId,
        courseId,
        slug,
        instructorId
      )
    },
    async restartQuiz() {
      this.isLoading = true
      await this.$store.dispatch('quizzes/restartQuiz')
      await this.$store.dispatch(
        'quizzes/startQuizAttemptByQuizId',
        this.quiz.id
      )
      this.currentQuestion = this.attempt.questions.questions[0]
      this.selectedAnswers = []
      this.finalScore = {}
      this.finalTime = null
      this.isQuizPassed = false
      this.hasAnsweredAllQuestions = false
      this.isQuizComplete = false
      this.isLoading = false
    },
  },
  created() {
    const body = document.querySelector('body')
    body.classList.add('modal-open')
  },
  destroyed() {
    this.$store.dispatch('quizzes/clearQuizStore', '')
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
}
</script>

<style lang="scss">
.quiz-modal {
  &__modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--white);
    z-index: 205;
    overflow-y: scroll;
  }
  &__wrapper {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    z-index: 206;
    position: relative;
  }
  &__content-wrapper {
    display: flex;
    margin: 0 auto;
    flex-wrap: wrap;
    max-width: 2800px;
    padding: 200px 50px;
    @include desktop {
      flex-wrap: nowrap;
    }
  }
  &__question {
    width: 100%;
    @include desktop {
      width: 40%;
    }
  }
  &__answers {
    width: 100%;
    @include desktop {
      width: 60%;
    }
  }
  &__completed {
    width: 100%;
  }
  .progress-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    padding: 0;
    z-index: 204;
    &__wrapper {
      border-radius: 0;
      padding: 0;
      margin: 0;
      background-color: #fff;
      text-align: center;
      width: 100%;
    }
    &__bar {
      height: 10px;
      &:after {
        display: none;
      }
    }
  }
  &__loader-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }
  &__loader {
    width: 5rem;
  }
}
</style>
